import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useAppTheme } from '../../context/ThemeContext';
import { themes } from '../../styles/theme';

const SwitcherContainer = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing.xl};
  right: ${({ theme }) => theme.spacing.xl};
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    top: ${({ theme }) => theme.spacing.md};
    right: ${({ theme }) => theme.spacing.md};
    /* Removed flex-wrap because we hide the extra items on mobile */
  }
`;

const TextButton = styled(motion.button)<{ $active?: boolean }>`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.textSecondary)};
  background: transparent;
  border: none;
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.xs} 0;
  position: relative;
  transition: color 0.3s ease;

  &::before {
    content: '[';
    opacity: ${({ $active }) => ($active ? 1 : 0)};
    margin-right: 4px;
    color: ${({ theme }) => theme.colors.textSecondary};
    transition: opacity 0.2s;
  }

  &::after {
    content: ']';
    opacity: ${({ $active }) => ($active ? 1 : 0)};
    margin-left: 4px;
    color: ${({ theme }) => theme.colors.textSecondary};
    transition: opacity 0.2s;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }

  &:hover::before,
  &:hover::after {
    opacity: 1;
  }
`;

const Divider = styled.span`
  color: ${({ theme }) => theme.colors.border};
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.75rem;
`;

const ThemeGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const DesktopOnlyWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

export const ThemeSwitcher: React.FC = () => {
  const { activeTheme, mode, setTheme, setMode, availableThemes } = useAppTheme();

  return (
    <SwitcherContainer>
      <ThemeGroup>
        <TextButton
          $active={mode === 'dark'}
          onClick={() => setMode('dark')}
          whileTap={{ scale: 0.95 }}
        >
          DRK
        </TextButton>
        <Divider>/</Divider>
        <TextButton
          $active={mode === 'light'}
          onClick={() => setMode('light')}
          whileTap={{ scale: 0.95 }}
        >
          LGT
        </TextButton>
      </ThemeGroup>

      <DesktopOnlyWrapper>
        <Divider>|</Divider>

        <ThemeGroup>
          {availableThemes.map(themeKey => (
            <TextButton
              key={themeKey}
              $active={activeTheme === themeKey}
              onClick={() => setTheme(themeKey)}
              title={themes[themeKey][mode].name}
              style={{
                color: activeTheme === themeKey ? themes[themeKey][mode].colors.primary : undefined,
              }}
            >
              {themeKey}
            </TextButton>
          ))}
        </ThemeGroup>
      </DesktopOnlyWrapper>
    </SwitcherContainer>
  );
};
